import { isArray, toJSON } from '@rehaje/shared'

import { UnknownSchema } from '../constants'
import {
  SchemaCustomError,
  SchemaDeprecatedWarning,
  SchemaRequiredError,
  SchemaTypeError
} from '../errors'
import { TypedDefinition } from './TypedDefinition'

import type { SchemaObjectSpecific, TypedSchema, TypedSpecific } from '../types'
import type { TPrimitive } from '@rehaje/shared'

/**
 * ADT Schema 类型定义 SPI 基类，主要用于扩展各种业务 ADT Schema，提升系统类型推导天花板
 * 实现 ADT Schema 的扩展会比较复杂，建议多参考官方 Schema 扩展源码写法
 * name 属性必须遵循 Schema.{name}
 */
export abstract class SchemaDefinition extends TypedDefinition {
  /**
   * 关联 TypedDefinition
   */
  get definition() {
    return this.__r_definitions[this.name?.replace('Schema.', '')]
  }

  /**
   * 接口名称
   */
  get interface(): string {
    return this.name.split('.').pop() as string
  }

  __r_hoistMetadata<T extends TypedSchema>(
    target: T = UnknownSchema as T,
    source?: TypedSchema
  ): T {
    if (!source?.type) return target
    const result = { ...target } as any
    for (const key of Object.keys(source)) {
      if (
        result[key] == null &&
        (key === 'name' ||
          key === 'deprecated' ||
          key === 'description' ||
          key === 'readOnly' ||
          key === 'required' ||
          key === 'title' ||
          key === 'writeOnly' ||
          key === 'fallback' ||
          key === 'nullable' ||
          key === 'example')
      ) {
        result[key] = source[key]
      }
    }
    return result
  }

  __r_inferTypeScript(schema: TypedSchema, path: PropertyKey[]): string
  __r_inferTypeScript(schema: any, path: PropertyKey[]): string {
    const defStore = this.__r_store.definition
    if (schema?.value !== undefined) schema = { ...schema, required: true }
    return defStore.__r_injectPathParent(
      () => defStore.inferTypeScript(schema),
      path
    )
  }

  /**
   * 合并默认值
   * @param target
   * @param schema
   * @param initialize
   * @returns
   */
  defaultsMerge(target: any, schema: TypedSchema, initialize: boolean): any {
    return this.__r_store.definition.defaultsMergeWithSchema(
      target,
      schema,
      initialize
    )
  }

  /**
   * 下钻查询Schema
   * @example
   * // 重载示例
   * override getIn(schema: SchemaStringSpecific, path: PropertyKey[]) {
   *   return schema
   * }
   * @param schema: 指定 schema
   * @param path: 查询路径
   */
  getIn(schema: TypedSchema, path?: PropertyKey[]): TypedSchema {
    return this.__r_store.definition.getInWithSchema(schema, path)
  }

  /** 协同推导器(需要重载)
   * @example
   * // 重载示例
   * override infer(target: any,schema: StringSchemaSpecific | undefined, path: PropertyKey[]) {
   *   return {
   *     ...schema,
   *     type: 'Schema.String',
   *     default: target
   *   } as const
   * }
   * @param target: 参考数据
   * @param schema: 参考 Schema
   * @param path: 当前路径
   */
  infer(
    target: any,
    schema: TypedSchema | undefined,
    path?: PropertyKey[],
    parent?: any
  ): TypedSchema {
    const defStore = this.__r_store.definition
    return defStore.__r_injectPathParent(
      () => defStore.inferWithSchema(target, schema),
      path,
      parent
    )
  }

  /**
   * 无值推导器(需要重载)，主要用于函数参数场景的推导
   * @param schema
   * @param path
   */
  inferOnlySchema(
    schema: TypedSchema,
    path: PropertyKey[],
    parent?: any
  ): TypedSchema {
    const defStore = this.__r_store.definition
    return defStore.__r_injectPathParent(
      () => defStore.inferOnlySchema(schema),
      path,
      parent
    )
  }

  /**
   * 真实数据推导，主要用于没有 schema 描述场景推导
   */
  inferShapeSchema(target: any): TypedSchema {
    return this.__r_store.definition.inferShapeSchema(target)
  }

  inferShapeTypeScript(target: any): string {
    let shapeSchema: TypedSchema
    const json = toJSON(target)
    if (typeof json?.type === 'string') {
      const { type, ...shape } = json
      shapeSchema = this.inferShapeSchema(shape) as SchemaObjectSpecific
      shapeSchema.value = json
      shapeSchema.properties?.unshift({
        name: 'type',
        required: true,
        type: 'Schema.String',
        value: type
      })
    } else {
      shapeSchema = this.inferShapeSchema(target)
    }
    return this.__r_inferTypeScript(shapeSchema, [])
  }

  /** TypeScript 协同推导器(需要重载)
   * @example
   * // 重载示例
   * override infer(target: any,schema: StringSchemaSpecific | undefined, path: PropertyKey[]) {
   *   return `${schema.name}: ${target ?? 'string'}`
   * }
   * @param schema: 参考 Schema
   * @param path: 当前路径
   */
  inferTypeScript(schema: TypedSchema, path: PropertyKey[]): string {
    return this.__r_inferTypeScript(schema, path)
  }

  /**
   * 判断指定 ADT 协议是否是当前 Schema 的关联类型，比如 Element 的 关联类型是 Schema.Element
   */
  isRelationSpecific<T extends TypedSpecific = TypedSpecific>(
    specific?: any
  ): specific is T {
    if (!specific) return false
    const definition = this.__r_definitions[specific.type]
    if (definition) {
      const relationTo = definition.relationTo
      return isArray(relationTo)
        ? relationTo.includes(this.name)
        : relationTo === this.name
    }
    return false
  }

  /**
   * 修剪数据
   * @param target
   * @param schema
   * @param path
   * @param parent
   */
  prune(
    target: any,
    schema: TypedSchema,
    path?: PropertyKey[],
    parent?: any
  ): any {
    return this.__r_store.definition.pruneWithSchema(target, schema, path, parent)
  }

  /**
   * 测试是否符合 Schema，一般不重载 test，重载 validate
   * @param target: 参考数据
   * @param schema: 参考 Schema
   * @param path: 当前路径，如果没有发生下钻可以不用传
   * @param parent 父对象，如果没有发生下钻可以不用传
   * @param recursion 是否是递归校验，如果没有变化可以不用传
   */
  test(
    target: any,
    schema: TypedSchema,
    path?: PropertyKey[],
    parent?: any,
    recursion?: boolean
  ): boolean {
    const defStore = this.__r_store.definition
    const errors = defStore.__r_injectPathParentRecursion(
      () => defStore.validateWithSchema(target, schema, recursion, false),
      path,
      parent,
      recursion
    )
    return errors.length === 0
  }

  /**
   * 基于 Schema 转换指定数据
   * @example
   * // 重载示例
   * override transform(target: any, schema: TypedSchema, path: PropertyKey[]) {
   *   return String(target)
   * }
   * @param target: 参考数据
   * @param schema: 参考 Schema
   * @param path: 当前路径，如果没有发生下钻可以不用传
   * @param parent 父对象，如果没有发生下钻可以不用传
   */
  transform(
    target: any,
    schema: TypedSchema,
    path?: PropertyKey[],
    parent?: any
  ): any {
    const defStore = this.__r_store.definition
    return defStore.__r_injectPathParent(
      () => defStore.transformWithSchema(target, schema),
      path,
      parent
    )
  }

  /**
   * 校验是否符合 Schema，校验过程中发生的错误都会设置到对应节点的 errors 中
   * 可以重载以实现自定义校验
   *
   * @example
   * override validate(target: any, schema: TypedSchema, path: PropertyKey[], parent?: any) {
   *   if (typeof target !== 'string') {
   *     this.raise(new SchemaTypeError('String', target))
   *   }
   * }
   * @param target 参考数据
   * @param schema 参考 Schema
   * @param path 当前路径，如果没有发生下钻可以不用传
   * @param parent 父对象，如果没有发生下钻可以不用传
   * @param recursion 是否是递归校验，如果没有变化可以不用传
   */
  validate(
    target: any,
    schema: TypedSchema,
    path?: PropertyKey[],
    parent?: any,
    recursion?: boolean
  ): void {
    const defStore = this.__r_store.definition
    defStore.__r_injectPathParentRecursion(
      () => defStore.validateWithSchema(target, schema, recursion),
      path,
      parent,
      recursion
    )
  }

  /**
   * 校验 Schema 元数据，返回值为是否需要中断校验
   * @param target 值
   * @param schema Schema
   * @param typeChecker 类型检查器
   * @param requiredChecker 必填检查器
   * @returns 是否需要中断校验
   */
  validateMetadata(
    target: any,
    schema: any,
    typeChecker?: ((target: any) => boolean) | boolean | TPrimitive,
    requiredChecker?: (target: any) => boolean
  ): boolean
  validateMetadata(
    target: any,
    schema: TypedSchema,
    typeChecker: ((target: any) => boolean) | boolean | TPrimitive = true,
    requiredChecker?: (target: any) => boolean
  ): boolean {
    if (this.__r_store.options.disableTypeChecker) return true
    if (
      typeof target === 'object' &&
      target !== null &&
      (target.type === 'JSExpression' ||
        target.type === 'Mustache' ||
        target.type === 'Expression')
    )
      return true
    if (typeof typeChecker === 'string')
      typeChecker = typeof target === typeChecker
    if (typeof typeChecker === 'function') typeChecker = typeChecker(target)
    if (typeChecker) {
      if (schema.required && requiredChecker?.(target) === false) {
        // @ts-ignore
        const requiredMessage = schema.requiredMessage
        if (requiredMessage) {
          this.raise(new SchemaCustomError(requiredMessage))
          return true
        }
        this.raise(new SchemaRequiredError(schema.name))
        return true
      }
      if (schema.deprecated) {
        this.raise(new SchemaDeprecatedWarning(schema.name))
      }
      return false
    }
    if (target === null && schema.nullable) return true
    if (target === undefined) {
      if (schema.required) this.raise(new SchemaRequiredError(schema.name))
      return true
    }
    this.raise(new SchemaTypeError(schema.name))
    return true
  }
}
